import { useState, type ChangeEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, uploadPlatformAsset } from "./api.js";

interface PlatformImage {
  id: string;
  name: string;
  clientId: string | null;
  mimeType: string;
}

export function StaticImagePicker({
  assetId,
  onChange,
  onBusyChange,
  busy,
}: {
  assetId: string;
  busy: boolean;
  onChange(id: string): void;
  onBusyChange(busy: boolean): void;
}) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const images = useQuery({
    queryKey: ["admin", "assets"],
    queryFn: () => api<PlatformImage[]>("/api/v1/admin/assets"),
  });
  const limits = useQuery({
    queryKey: ["admin", "assets", "upload-limits"],
    queryFn: () => api<{ imageMb: number }>("/api/v1/admin/assets/upload-limits"),
  });
  const choices = (images.data ?? []).filter(
    (image) =>
      image.clientId === null && ["image/jpeg", "image/png", "image/webp"].includes(image.mimeType),
  );
  async function upload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !limits.data) return;
    setError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (!file.size || file.size > limits.data.imageMb * 1024 * 1024) {
      setError(`Choose an image up to ${limits.data.imageMb} MB.`);
      return;
    }
    setUploading(true);
    onBusyChange(true);
    try {
      const uploaded = await uploadPlatformAsset<PlatformImage>(file);
      queryClient.setQueryData<PlatformImage[]>(["admin", "assets"], (current) => [
        uploaded,
        ...(current ?? []),
      ]);
      onChange(uploaded.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Image upload failed. Please retry.");
    } finally {
      setUploading(false);
      onBusyChange(false);
    }
  }
  return (
    <fieldset>
      <legend>Static image</legend>
      <label>
        <span>Platform image</span>
        <select
          value={assetId}
          disabled={busy || uploading || images.isPending}
          onChange={(event) => {
            setError("");
            onChange(event.target.value);
          }}
        >
          <option value="">Choose a platform image</option>
          {choices.map((image) => (
            <option key={image.id} value={image.id}>
              {image.name}
            </option>
          ))}
          {assetId && !choices.some((image) => image.id === assetId) ? (
            <option value={assetId}>Selected image (unavailable in library)</option>
          ) : null}
        </select>
      </label>
      <label>
        <span>Upload static image</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy || uploading || !limits.data}
          onChange={(event) => {
            void upload(event);
          }}
        />
      </label>
      <p className="field-help">
        Choose a platform image or upload one here
        {limits.data ? ` (up to ${limits.data.imageMb} MB)` : ""}. It is saved in the slide type;
        clients cannot replace it. Use Media fit to contain or crop it.
      </p>
      {images.isPending ? <p role="status">Loading platform images…</p> : null}
      {uploading ? <p role="status">Uploading image…</p> : null}
      {!images.isPending && !choices.length ? (
        <p>No platform images yet. Upload an image above.</p>
      ) : null}
      {images.isError || limits.isError ? (
        <div role="alert">
          <p>Could not load the image library or upload limits.</p>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => {
              void images.refetch();
              void limits.refetch();
            }}
          >
            Retry image library
          </button>
        </div>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
